import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  VizPortalDQWDataCatalogProviderProps,
  WarningStatusChip,
  WarningStatusProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from "@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator";
import * as React from 'react';
import { Query } from 'react-apollo';

import {
  PublishedDatasourceLineageUpstreamDataQualityWarningsQuery,
  PublishedDatasourceLineageUpstreamDataQualityWarningsQueryVariables,
} from '../../../gen-src/queries/PublishedDatasourceLineageUpstreamDataQualityWarningsQuery';
import { PublishedDatasourceLineageUpstreamDataQualityWarningsQueryGql } from '../../queries/PublishedDatasourceLineageUpstreamDataQualityWarningsQueryGql';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { renderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';

interface LineageWarningPublishedDatasourceProps {
  readonly asset: AssetMarkedByIndicator;
  readonly vizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddablePublishedDatasourceDataQualityWarningProps =
  VizPortalDQWDataCatalogProviderProps & LineageWarningPublishedDatasourceProps;

export function EmbeddablePublishedDatasourceDataQualityWarning(props: EmbeddablePublishedDatasourceDataQualityWarningProps): JSX.Element {
  const { featureFlags: { publishedConnectionsLineageEnabled }, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<WarningQuery, WarningVariables>
          query={PublishedDatasourceLineageUpstreamDataQualityWarningsQueryGql}
          variables={{ vizportalUrlId: props.vizportalUrlId, first: 10, offset: 0, publishedConnectionsLineageEnabled }}
        >
          {result => renderQueryResult(result, {
            renderLoading: () => renderWarnings(urls, { ...props }),
            renderData: data => renderWarnings(urls, { ...props }, data),
          })}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

type WarningQuery = PublishedDatasourceLineageUpstreamDataQualityWarningsQuery;
type WarningVariables = PublishedDatasourceLineageUpstreamDataQualityWarningsQueryVariables;

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddablePublishedDatasourceDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  data?: WarningQuery,
): JSX.Element | null {
  const datasource = data && data.publishedDatasources ? data.publishedDatasources[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive);
  if (!datasource && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = datasource
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(datasource.id, datasource.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings;

  const props: WarningStatusProps = {
    subjectArea: 'PublishedDatasource',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
  };

  return <WarningStatusChip {...props} />;
}
